import {
  get,
  ref,
  set,
  update,
  push,
  onValue,
  onChildAdded,
  type DataSnapshot,
} from "firebase/database";
import { database } from "../firebase/firebaseConfig";
import { AuthenticationService } from "../authenticationService/AuthenticationService";
import { SessionTeardownService } from "../sessionTeardownService/SessionTeardownService";
import {
  Partnership,
  PartnershipStatus,
  PartnershipHelper,
} from "../../domain/models/Partnership";
import { PartnershipConverter } from "./converters/PartnershipConverter";
import {
  PartnershipErrorConverter,
  PartnershipErrorCause,
} from "./converters/PartnershipErrorConverter";
import { InvitationCode } from "./utils/InvitationCode";
import { CoreError } from "../../domain/models/CoreError";
import { User } from "../../domain/models/User";
import { UserConverter } from "../userService/converters/UserConverter";
import { PartnershipAPIService } from "./api/PartnershipAPIService";

export type CoupleToPartnerResult = {
  status: "pending";
  partnerIdentifier: string;
};

export type SuperlikeStatus = "pending" | "liked" | "disliked";

export interface PartnershipSuperlike {
  cardIdentifier: string;
  fromUserId: string;
  createdAt: number;
  status: SuperlikeStatus;
}

export interface PendingSuperlikeObserverPayload {
  cardIdentifier: string;
  createdAt: number;
}

/**
 * PartnershipService handles all partnership-related operations including:
 * - Creating, loading, and managing partnerships
 * - Invitation code generation and validation
 * - Coupling and disconnecting partners (via API)
 * - Real-time observers for partnership state changes
 * - Match tracking
 */
export class PartnershipService {
  // Singleton instance
  private static instance: PartnershipService;

  private apiService = PartnershipAPIService.getInstance();

  // Private constructor to enforce singleton usage
  private constructor() {}

  // Global accessor to retrieve the singleton
  public static getInstance(): PartnershipService {
    if (!PartnershipService.instance) {
      PartnershipService.instance = new PartnershipService();
    }
    return PartnershipService.instance;
  }

  // ==================== Partnership Creation ====================

  /**
   * Creates a new pending partnership with an invitation code.
   * Used when a user signs up or wants to create a new partnership.
   *
   * @param userId The user identifier
   * @param invitationCode Optional invitation code (generated if not provided)
   * @returns Promise resolving to the created Partnership
   */
  public async createPartnership(
    userId: string,
    invitationCode?: string,
  ): Promise<Partnership> {
    try {
      const partnershipRef = push(ref(database, "partnerships"));
      const partnershipId = partnershipRef.key;

      if (!partnershipId) {
        throw PartnershipErrorConverter.createPartnershipError(
          PartnershipErrorCause.InvalidPartnershipData,
          "Failed to generate partnership identifier",
        );
      }

      // Generate invitation code if not provided
      const code =
        invitationCode || (await this.generateUniqueInvitationCode());

      const partnership = PartnershipHelper.createPending(partnershipId, code);

      const updates = PartnershipConverter.toFirebaseForCreation(
        partnership,
        userId,
        false,
      );

      await update(ref(database), updates);

      return partnership;
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  /**
   * Creates a play-alone partnership for solo play.
   *
   * @param userId The user identifier
   * @param invitationCode The invitation code for this partnership
   * @returns Promise resolving to the created Partnership
   */
  public async createPlayAlonePartnership(
    userId: string,
    invitationCode: string,
  ): Promise<Partnership> {
    try {
      const partnershipRef = push(ref(database, "partnerships"));
      const partnershipId = partnershipRef.key;

      if (!partnershipId) {
        throw PartnershipErrorConverter.createPartnershipError(
          PartnershipErrorCause.InvalidPartnershipData,
          "Failed to generate partnership identifier",
        );
      }

      const partnership = PartnershipHelper.createPlayingAlone(
        partnershipId,
        invitationCode,
      );

      const updates = PartnershipConverter.toFirebaseForCreation(
        partnership,
        userId,
        true,
      );

      await update(ref(database), updates);

      return partnership;
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  /**
   * Creates a partnership with a fictional character. The character occupies
   * partner2, the partnership status is 'character', and the character is
   * selected for play in the same atomic update. Reconnecting after a
   * disconnect creates a brand-new partnership (fresh deck and matches),
   * mirroring real-partner reconnects.
   *
   * @param userId The user identifier
   * @param characterId The fictional character identifier (char_…)
   * @returns Promise resolving to the new partnership identifier
   */
  public async createCharacterPartnership(
    userId: string,
    characterId: string,
  ): Promise<string> {
    try {
      const partnershipRef = push(ref(database, "partnerships"));
      const partnershipId = partnershipRef.key;

      if (!partnershipId) {
        throw PartnershipErrorConverter.createPartnershipError(
          PartnershipErrorCause.InvalidPartnershipData,
          "Failed to generate partnership identifier",
        );
      }

      const updates = PartnershipConverter.createCharacterToFirebase(
        userId,
        characterId,
        partnershipId,
        Date.now() / 1000,
      );

      await update(ref(database), updates);

      return partnershipId;
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  /**
   * Selects a fictional character for play. The character's partnership is
   * resolved by the deck session from selectedCharacter + the character
   * partnerships category.
   *
   * @param userId The user identifier
   * @param characterId The fictional character identifier
   * @returns Promise that resolves when complete
   */
  public async selectCharacter(
    userId: string,
    characterId: string,
  ): Promise<void> {
    try {
      const updates: Record<string, unknown> = {};
      updates[`users/${userId}/profile/selectedCharacter`] = characterId;
      updates[`users/${userId}/profile/isPlayingAlone`] = true;
      await update(ref(database), updates);
    } catch (error) {
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  /**
   * Disconnects from a fictional-character partnership. Fully client-side —
   * characters have no server side, so no Cloud Function is involved.
   *
   * @param partnershipId The partnership identifier
   * @param userId The user identifier
   * @param clearSelectedCharacter Whether the user's selectedCharacter points
   *   at this character and must be cleared
   * @returns Promise that resolves when complete
   */
  public async disconnectFromCharacterPartnership(
    partnershipId: string,
    userId: string,
    clearSelectedCharacter: boolean,
  ): Promise<void> {
    try {
      const updates = PartnershipConverter.disconnectCharacterToFirebase(
        partnershipId,
        userId,
        clearSelectedCharacter,
      );

      await update(ref(database), updates);
    } catch (error) {
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  // ==================== Partnership Loading ====================

  /**
   * Loads a partnership by its identifier.
   *
   * @param partnershipId The partnership identifier
   * @param loggedInUserId The current logged-in user's identifier
   * @returns Promise resolving to the Partnership
   */
  public async loadPartnership(
    partnershipId: string,
    loggedInUserId: string,
  ): Promise<Partnership> {
    try {
      const snapshot = await get(
        ref(database, `partnerships/${partnershipId}/profile`),
      );

      if (!snapshot.exists()) {
        throw PartnershipErrorConverter.createPartnershipError(
          PartnershipErrorCause.PartnershipNotFound,
          `Partnership with identifier ${partnershipId} does not exist`,
        );
      }

      const data = snapshot.val();
      const partnership = PartnershipConverter.fromFirebase(
        data,
        partnershipId,
        loggedInUserId,
      );

      if (!partnership) {
        throw PartnershipErrorConverter.createPartnershipError(
          PartnershipErrorCause.InvalidPartnershipData,
          "Failed to parse partnership data from Firebase",
        );
      }

      // Load matched cards
      const matchedCards = await this.loadMatches(partnershipId);
      partnership.matchedCardsByIdentifier = matchedCards;

      return partnership;
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  /**
   * Loads matched card identifiers for a partnership.
   *
   * @param partnershipId The partnership identifier
   * @returns Promise resolving to matched card timestamps by identifier
   */
  public async loadMatches(
    partnershipId: string,
  ): Promise<Record<string, number>> {
    try {
      const snapshot = await get(
        ref(database, `partnerships/${partnershipId}/matches`),
      );

      if (!snapshot.exists()) {
        return {};
      }

      const data = snapshot.val();
      return PartnershipConverter.matchedCardsFromFirebase(data);
    } catch (error) {
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  /**
   * Loads a partner's user profile.
   *
   * @param partnerId The partner's user identifier
   * @returns Promise resolving to the partner's User profile
   */
  public async loadPartner(partnerId: string): Promise<User> {
    try {
      const snapshot = await get(ref(database, `users/${partnerId}/profile`));

      if (!snapshot.exists()) {
        throw PartnershipErrorConverter.createPartnershipError(
          PartnershipErrorCause.PartnerNotFound,
          `Partner with identifier ${partnerId} does not exist`,
        );
      }

      const data = snapshot.val();
      const partner = UserConverter.fromFirebase(data, partnerId);

      if (!partner) {
        throw PartnershipErrorConverter.createPartnershipError(
          PartnershipErrorCause.PartnerNotFound,
          "Failed to parse partner data from Firebase",
        );
      }

      return partner;
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  // ==================== Invitation Codes ====================

  /**
   * Generates a unique invitation code that doesn't already exist.
   *
   * @returns Promise resolving to a unique invitation code
   */
  public async generateUniqueInvitationCode(): Promise<string> {
    const maxAttempts = 30;
    let attempts = 0;

    while (attempts < maxAttempts) {
      const code = InvitationCode.generateStandard();

      // Check if code already exists
      const snapshot = await get(ref(database, `invitationCodes/${code}`));

      if (!snapshot.exists()) {
        return code;
      }

      attempts++;
    }

    throw PartnershipErrorConverter.createPartnershipError(
      PartnershipErrorCause.InvalidInvitationCode,
      "Failed to generate unique invitation code after multiple attempts",
    );
  }

  /**
   * Generates and saves a new invitation code for an existing partnership.
   *
   * @param partnershipId The partnership identifier
   * @returns Promise resolving to the generated invitation code
   */
  public async generateInvitationCodeForPartnership(
    partnershipId: string,
  ): Promise<string> {
    try {
      const code = await this.generateUniqueInvitationCode();

      const updates: Record<string, unknown> = {};
      updates[`invitationCodes/${code}/partnership`] = partnershipId;
      updates[`partnerships/${partnershipId}/profile/invitationCode`] = code;

      await update(ref(database), updates);

      return code;
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  /**
   * Generates and saves a new invitation code for a user.
   *
   * @param userId The user identifier
   * @returns Promise resolving to the generated invitation code
   */
  public async generateInvitationCodeForUser(userId: string): Promise<string> {
    try {
      const code = await this.generateUniqueInvitationCode();

      await set(ref(database, `users/${userId}/profile/invitationCode`), code);

      return code;
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  /**
   * Looks up a partnership ID by invitation code.
   *
   * @param invitationCode The invitation code to look up
   * @returns Promise resolving to the partnership ID or null if not found
   */
  public async getPartnershipIdByInvitationCode(
    invitationCode: string,
  ): Promise<string | null> {
    try {
      const normalizedCode = InvitationCode.normalize(invitationCode);

      const snapshot = await get(
        ref(database, `invitationCodes/${normalizedCode}/partnership`),
      );

      if (!snapshot.exists()) {
        return null;
      }

      return snapshot.val();
    } catch (error) {
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  // ==================== Partnership Status Management ====================

  /**
   * Selects a partnership as the currently playing partnership.
   * Updates status to Selected and sets all other active partnerships to Active.
   *
   * @param userId The user identifier
   * @param partnershipId The partnership to select
   * @param otherActivePartnershipIds Array of other active partnership IDs to demote
   * @returns Promise that resolves when complete
   */
  public async selectPartnership(
    userId: string,
    partnershipId: string,
    otherActivePartnershipIds: string[] = [],
  ): Promise<void> {
    try {
      const updates: Record<string, unknown> = {};

      // Set selected partnership to Selected status
      const selectedUpdates = PartnershipConverter.statusToFirebase(
        partnershipId,
        userId,
        PartnershipStatus.Selected,
      );
      Object.assign(updates, selectedUpdates);

      // Set other active partnerships to Active status
      otherActivePartnershipIds.forEach((otherId) => {
        const activeUpdates = PartnershipConverter.statusToFirebase(
          otherId,
          userId,
          PartnershipStatus.Active,
        );
        Object.assign(updates, activeUpdates);
      });

      // Ensure user is not playing alone and no character is selected
      updates[`users/${userId}/profile/isPlayingAlone`] = false;
      updates[`users/${userId}/profile/selectedCharacter`] = null;

      await update(ref(database), updates);
    } catch (error) {
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  /**
   * Selects the playing alone mode.
   *
   * @param userId The user identifier
   * @returns Promise that resolves when complete
   */
  public async selectPlayingAlone(userId: string): Promise<void> {
    try {
      const updates: Record<string, unknown> = {};
      updates[`users/${userId}/profile/isPlayingAlone`] = true;
      updates[`users/${userId}/profile/selectedCharacter`] = null;
      await update(ref(database), updates);
    } catch (error) {
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  /**
   * Updates a partnership's status.
   *
   * @param partnershipId The partnership identifier
   * @param userId The user identifier
   * @param status The new status
   * @returns Promise that resolves when complete
   */
  public async updatePartnershipStatus(
    partnershipId: string,
    userId: string,
    status: PartnershipStatus,
  ): Promise<void> {
    try {
      const updates = PartnershipConverter.statusToFirebase(
        partnershipId,
        userId,
        status,
      );

      await update(ref(database), updates);
    } catch (error) {
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  // ==================== Partnership Disconnection ====================

  /**
   * Disconnects from a partner, marking the partnership as invalid.
   *
   * @param partnershipId The partnership identifier
   * @param userId The user identifier
   * @param partnerId Optional partner identifier
   * @returns Promise that resolves when complete
   */
  public async disconnectFromPartner(
    partnershipId: string,
    userId: string,
    partnerId?: string,
  ): Promise<void> {
    try {
      const updates = PartnershipConverter.disconnectToFirebase(
        partnershipId,
        userId,
        partnerId,
      );

      await update(ref(database), updates);
    } catch (error) {
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  /**
   * Deletes all pending partnerships for a user.
   *
   * @param userId The user identifier
   * @returns Promise that resolves when complete
   */
  public async deletePendingPartnerships(userId: string): Promise<void> {
    try {
      // Load pending partnerships
      const snapshot = await get(
        ref(database, `users/${userId}/profile/partnerships/pending`),
      );

      if (!snapshot.exists()) {
        return; // No pending partnerships
      }

      const pendingIds = Object.keys(snapshot.val());

      // Load invitation codes for each pending partnership
      const pendingPartnerships: Array<{ id: string; invitationCode: string }> =
        [];

      for (const id of pendingIds) {
        const codeSnapshot = await get(
          ref(database, `partnerships/${id}/profile/invitationCode`),
        );

        if (codeSnapshot.exists()) {
          pendingPartnerships.push({
            id,
            invitationCode: codeSnapshot.val(),
          });
        }
      }

      // Create and apply deletion updates
      const updates = PartnershipConverter.deletePendingPartnershipsToFirebase(
        userId,
        pendingPartnerships,
      );

      await update(ref(database), updates);
    } catch (error) {
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  // ==================== Match Management ====================

  /**
   * Stores matched cards for a partnership.
   *
   * @param partnershipId The partnership identifier
   * @param cardIdentifiers Array of card identifiers that matched
   * @param timestamp The match timestamp (defaults to now)
   * @returns Promise that resolves when complete
   */
  public async storeMatchedCards(
    partnershipId: string,
    cardIdentifiers: string[],
    timestamp: number = Date.now(),
  ): Promise<void> {
    try {
      const updates = PartnershipConverter.matchedCardsToFirebase(
        partnershipId,
        cardIdentifiers,
        timestamp,
      );

      await update(ref(database), updates);
    } catch (error) {
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  /**
   * Stores matched cards with individual timestamps (seconds). Used when
   * committing held character matches whose reveal times differ per card.
   *
   * @param partnershipId The partnership identifier
   * @param timestampsByCardId Record of card identifier to match timestamp
   * @returns Promise that resolves when complete
   */
  public async storeMatchedCardsWithTimestamps(
    partnershipId: string,
    timestampsByCardId: Record<string, number>,
  ): Promise<void> {
    try {
      const updates = PartnershipConverter.matchedCardsWithTimestampsToFirebase(
        partnershipId,
        timestampsByCardId,
      );

      await update(ref(database), updates);
    } catch (error) {
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  // ==================== Visit Tracking ====================

  /**
   * Saves the last visit timestamp for a partnership along with deck state.
   *
   * @param userId The user identifier
   * @param partnershipId The partnership identifier
   * @param timestamp The visit timestamp (defaults to now)
   * @param deckTopCardId Optional deck top card identifier
   * @param numberOfCardsLeft Optional number of cards left in deck
   * @returns Promise that resolves when complete
   */
  public async saveLastPartnershipVisit(
    userId: string,
    partnershipId: string,
    timestamp: number = Date.now(),
    deckTopCardId?: string | null,
    numberOfCardsLeft?: number,
  ): Promise<void> {
    try {
      const updates = PartnershipConverter.visitToFirebase(
        userId,
        partnershipId,
        timestamp,
        deckTopCardId,
        numberOfCardsLeft,
      );

      await update(ref(database), updates);
    } catch (error) {
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  public async saveDeckState(
    userId: string,
    partnershipId: string,
    deckTopCardId: string | null | undefined,
    numberOfCardsLeft: number,
  ): Promise<void> {
    try {
      const updates = PartnershipConverter.deckStateToFirebase(
        userId,
        partnershipId,
        deckTopCardId,
        numberOfCardsLeft,
      );

      await update(ref(database), updates);
    } catch (error) {
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  /**
   * Saves the last matches screen visit timestamp for a partnership.
   *
   * @param userId The user identifier
   * @param partnershipId The partnership identifier
   * @param timestamp The visit timestamp (defaults to now)
   * @returns Promise that resolves when complete
   */
  public async saveLastMatchesScreenVisit(
    userId: string,
    partnershipId: string,
    timestamp: number = Date.now(),
  ): Promise<void> {
    try {
      await set(
        ref(
          database,
          `users/${userId}/profile/partnershipsLastMatchesScreenVisit/${partnershipId}`,
        ),
        timestamp,
      );
    } catch (error) {
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  // ==================== Partnership Requests ====================

  /**
   * Removes a partnership request from the user's profile.
   *
   * @param userId The user identifier
   * @param requestingPartnerId The requesting partner's identifier
   * @returns Promise that resolves when complete
   */
  public async removePartnershipRequest(
    userId: string,
    requestingPartnerId: string,
  ): Promise<void> {
    try {
      await set(
        ref(
          database,
          `users/${userId}/profile/requests/${requestingPartnerId}`,
        ),
        null,
      );
    } catch (error) {
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  // ==================== Observers ====================

  /**
   * Observes changes to a partnership profile in real-time.
   *
   * @param partnershipId The partnership identifier to observe
   * @param loggedInUserId The current logged-in user's identifier
   * @param onPartnershipChanged Callback invoked whenever partnership data changes
   * @param onError Callback invoked when an error occurs
   * @returns Unsubscribe function to stop observing
   */
  public observePartnership(
    partnershipId: string,
    loggedInUserId: string,
    onPartnershipChanged: (partnership: Partnership) => void,
    onError: (error: CoreError) => void,
  ): () => void {
    const partnershipRef = ref(
      database,
      `partnerships/${partnershipId}/profile`,
    );

    const onValueChange = async (snapshot: DataSnapshot) => {
      try {
        if (!snapshot.exists()) {
          onError(
            PartnershipErrorConverter.createPartnershipError(
              PartnershipErrorCause.PartnershipNotFound,
              `Partnership with identifier ${partnershipId} does not exist`,
            ),
          );
          return;
        }

        const data = snapshot.val();
        const partnership = PartnershipConverter.fromFirebase(
          data,
          partnershipId,
          loggedInUserId,
        );

        if (!partnership) {
          onError(
            PartnershipErrorConverter.createPartnershipError(
              PartnershipErrorCause.InvalidPartnershipData,
              "Failed to parse partnership data from Firebase",
            ),
          );
          return;
        }

        // Load matched cards
        const matchedCards = await this.loadMatches(partnershipId);
        partnership.matchedCardsByIdentifier = matchedCards;

        onPartnershipChanged(partnership);
      } catch (error) {
        onError(PartnershipErrorConverter.domainModel(error));
      }
    };

    const onErrorCallback = (error: Error) => {
      if (
        SessionTeardownService.isTeardownInProgress() ||
        !AuthenticationService.getInstance().isUserLoggedIn
      ) {
        return;
      }
      onError(PartnershipErrorConverter.domainModel(error));
    };

    const unsubscribe = onValue(partnershipRef, onValueChange, onErrorCallback);

    return () => {
      unsubscribe();
    };
  }

  /**
   * Observes new matches for a partnership in real-time.
   *
   * @param partnershipId The partnership identifier
   * @param onMatchAdded Callback invoked when a new match is added
   * @param onError Callback invoked when an error occurs
   * @returns Unsubscribe function to stop observing
   */
  public observeMatches(
    partnershipId: string,
    onMatchAdded: (cardIdentifier: string, timestamp: number) => void,
    onError: (error: CoreError) => void,
  ): () => void {
    const matchesRef = ref(database, `partnerships/${partnershipId}/matches`);
    let isActive = true;

    const handleChildAdded = (snapshot: DataSnapshot) => {
      if (!isActive) {
        return;
      }
      try {
        // snapshot.key is only null for the root ref, never for a child.
        if (snapshot.exists() && snapshot.key !== null) {
          const cardIdentifier = snapshot.key;
          const timestamp = snapshot.val();
          onMatchAdded(cardIdentifier, timestamp);
        }
      } catch (error) {
        if (isActive) {
          onError(PartnershipErrorConverter.domainModel(error));
        }
      }
    };

    const onErrorCallback = (error: Error) => {
      if (!isActive) {
        return;
      }
      if (
        SessionTeardownService.isTeardownInProgress() ||
        !AuthenticationService.getInstance().isUserLoggedIn
      ) {
        return;
      }
      onError(PartnershipErrorConverter.domainModel(error));
    };

    const unsubscribe = onChildAdded(
      matchesRef,
      handleChildAdded,
      onErrorCallback,
    );

    return () => {
      isActive = false;
      unsubscribe();
    };
  }

  /**
   * Observes new partnership requests for a user in real-time.
   *
   * @param userId The user identifier
   * @param onRequestsChanged Callback invoked when requests change
   * @param onError Callback invoked when an error occurs
   * @returns Unsubscribe function to stop observing
   */
  public observePartnershipRequests(
    userId: string,
    onRequestsChanged: (requests: Record<string, number>) => void,
    onError: (error: CoreError) => void,
  ): () => void {
    const requestsRef = ref(database, `users/${userId}/profile/requests`);

    const onValueChange = (snapshot: DataSnapshot) => {
      try {
        if (!snapshot.exists()) {
          onRequestsChanged({});
          return;
        }

        const data = snapshot.val();

        // Ensure data is a valid object with string keys and number values
        if (typeof data === "object" && data !== null) {
          onRequestsChanged(data as Record<string, number>);
        } else {
          onRequestsChanged({});
        }
      } catch (error) {
        onError(PartnershipErrorConverter.domainModel(error));
      }
    };

    const onErrorCallback = (error: Error) => {
      if (
        SessionTeardownService.isTeardownInProgress() ||
        !AuthenticationService.getInstance().isUserLoggedIn
      ) {
        return;
      }
      onError(PartnershipErrorConverter.domainModel(error));
    };

    const unsubscribe = onValue(requestsRef, onValueChange, onErrorCallback);

    return () => {
      unsubscribe();
    };
  }

  /**
   * Observes partner's liked cards for a specific partnership.
   *
   * @param partnerId The partner's user identifier
   * @param partnershipId The partnership identifier
   * @param onLikedCardsChanged Callback invoked when liked cards change
   * @param onError Callback invoked when an error occurs
   * @returns Unsubscribe function to stop observing
   */
  public observePartnerLikedCards(
    partnerId: string,
    partnershipId: string,
    onLikedCardsChanged: (cardIdentifiers: string[]) => void,
    onError: (error: CoreError) => void,
  ): () => void {
    const likedCardsRef = ref(
      database,
      `users/${partnerId}/cards/${partnershipId}/liked`,
    );
    let isActive = true;

    const onValueChange = (snapshot: DataSnapshot) => {
      if (!isActive) {
        return;
      }
      try {
        if (!snapshot.exists()) {
          onLikedCardsChanged([]);
          return;
        }

        const data = snapshot.val();
        const cardIdentifiers = Object.keys(data);

        onLikedCardsChanged(cardIdentifiers);
      } catch (error) {
        if (isActive) {
          onError(PartnershipErrorConverter.domainModel(error));
        }
      }
    };

    const onErrorCallback = (error: Error) => {
      if (!isActive) {
        return;
      }
      if (
        SessionTeardownService.isTeardownInProgress() ||
        !AuthenticationService.getInstance().isUserLoggedIn
      ) {
        return;
      }
      onError(PartnershipErrorConverter.domainModel(error));
    };

    const unsubscribe = onValue(likedCardsRef, onValueChange, onErrorCallback);

    return () => {
      isActive = false;
      unsubscribe();
    };
  }

  public observeDeckTopCard(
    userId: string,
    partnershipId: string,
    onDeckTopCardChanged: (deckTopCardId: string | null) => void,
    onError: (error: CoreError) => void,
  ): () => void {
    const deckTopCardRef = ref(
      database,
      `users/${userId}/profile/partnershipsDeckTopCardIdentifiers/${partnershipId}`,
    );
    let isActive = true;

    const onValueChange = (snapshot: DataSnapshot) => {
      if (!isActive) {
        return;
      }
      try {
        if (!snapshot.exists()) {
          onDeckTopCardChanged(null);
          return;
        }
        const value = snapshot.val();
        onDeckTopCardChanged(typeof value === "string" ? value : null);
      } catch (error) {
        if (isActive) {
          onError(PartnershipErrorConverter.domainModel(error));
        }
      }
    };

    const onErrorCallback = (error: Error) => {
      if (!isActive) {
        return;
      }
      if (
        SessionTeardownService.isTeardownInProgress() ||
        !AuthenticationService.getInstance().isUserLoggedIn
      ) {
        return;
      }
      onError(PartnershipErrorConverter.domainModel(error));
    };

    const unsubscribe = onValue(deckTopCardRef, onValueChange, onErrorCallback);

    return () => {
      isActive = false;
      unsubscribe();
    };
  }

  // ==================== API-Based Operations ====================

  public async prefetchInvitationCodePolicy(): Promise<void> {
    await this.apiService.getInvitationCodePolicy();
  }

  /**
   * Couples the logged-in user to a partner using an invitation code.
   * This method calls the backend Cloud Function to validate and establish the partnership.
   *
   * Flow:
   * 1. Calls API to couple with partner
   * 2. If partnerIdentifier is returned and partnerAppVersion exists:
   *    - Partner is on newer version and the request stays pending
   * 3. If partnerIdentifier is returned but no partnerAppVersion:
   *    - Partner is on older version, load partnership immediately
   *    - Returns connected result with partner User object
   *
   * @param invitationCode The partner's invitation code
   * @returns Promise resolving to coupling result
   */
  public async coupleToPartnerWithInvitationCode(
    invitationCode: string,
  ): Promise<CoupleToPartnerResult> {
    try {
      const response = await this.apiService.coupleToPartner(invitationCode);

      const { partnerIdentifier } = response;

      if (!partnerIdentifier) {
        throw PartnershipErrorConverter.createPartnershipError(
          PartnershipErrorCause.InvalidInvitationCode,
          "Invalid invitation code or partner not found",
        );
      }

      return {
        status: "pending",
        partnerIdentifier,
      };
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  /**
   * Decides on a partnership request (accept or decline).
   * Calls the backend Cloud Function to process the decision.
   *
   * @param requestingPartnerIdentifier The identifier of the user who sent the request
   * @param isAccepted Whether to accept (true) or decline (false) the request
   * @returns Promise that resolves when the decision is processed
   */
  public async decideOnPartnershipRequest(
    requestingPartnerIdentifier: string,
    isAccepted: boolean,
  ): Promise<void> {
    try {
      await this.apiService.decidePartnershipRequest(
        requestingPartnerIdentifier,
        isAccepted,
      );
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  /**
   * Disconnects from a partner via API.
   * This method calls the backend Cloud Function to invalidate the partnership
   * on both sides and clean up related data.
   *
   * Note: This is the API-based disconnect that triggers backend logic.
   * The local Firebase updates are handled by disconnectFromPartner() method.
   *
   * @param partnerIdentifier The partner's user identifier
   * @param partnershipIdentifier The partnership identifier
   * @returns Promise that resolves when disconnection is complete
   */
  public async disconnectFromPartnerViaAPI(
    partnerIdentifier: string,
    partnershipIdentifier: string,
  ): Promise<void> {
    try {
      await this.apiService.disconnectFromPartner(
        partnerIdentifier,
        partnershipIdentifier,
      );
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  // ==================== Superlike Management ====================

  public async savePendingSuperlike(
    partnershipId: string,
    targetUserId: string,
    cardIdentifier: string,
    fromUserId: string,
    createdAt: number = Date.now() / 1000,
  ): Promise<void> {
    try {
      await set(
        ref(
          database,
          `partnerships/${partnershipId}/superlikes/${targetUserId}/${cardIdentifier}`,
        ),
        {
          fromUserId,
          createdAt,
          status: "pending",
        },
      );
    } catch (error) {
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  public async loadPendingSuperlikes(
    partnershipId: string,
    targetUserId: string,
  ): Promise<PartnershipSuperlike[]> {
    try {
      const snapshot = await get(
        ref(
          database,
          `partnerships/${partnershipId}/superlikes/${targetUserId}`,
        ),
      );
      if (!snapshot.exists()) {
        return [];
      }

      const data = snapshot.val();
      if (!data || typeof data !== "object") {
        return [];
      }

      const parsed: PartnershipSuperlike[] = [];
      Object.entries(data as Record<string, unknown>).forEach(
        ([cardIdentifier, raw]) => {
          if (!raw || typeof raw !== "object") {
            return;
          }
          const fromUserId = (raw as { fromUserId?: unknown }).fromUserId;
          const createdAt = (raw as { createdAt?: unknown }).createdAt;
          const status = (raw as { status?: unknown }).status;

          if (
            typeof fromUserId !== "string" ||
            typeof createdAt !== "number" ||
            !Number.isFinite(createdAt) ||
            status !== "pending"
          ) {
            return;
          }

          parsed.push({
            cardIdentifier,
            fromUserId,
            createdAt,
            status: "pending",
          });
        },
      );

      parsed.sort(
        (a, b) =>
          b.createdAt - a.createdAt ||
          a.cardIdentifier.localeCompare(b.cardIdentifier),
      );
      return parsed;
    } catch (error) {
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  public async updateSuperlikeStatus(
    partnershipId: string,
    targetUserId: string,
    cardIdentifier: string,
    status: Exclude<SuperlikeStatus, "pending">,
  ): Promise<void> {
    try {
      await set(
        ref(
          database,
          `partnerships/${partnershipId}/superlikes/${targetUserId}/${cardIdentifier}/status`,
        ),
        status,
      );
    } catch (error) {
      throw PartnershipErrorConverter.domainModel(error);
    }
  }

  public observePendingSuperlikes(
    partnershipId: string,
    targetUserId: string,
    onPendingSuperlikeAdded: (payload: PendingSuperlikeObserverPayload) => void,
    onError: (error: CoreError) => void,
  ): () => void {
    const superlikesRef = ref(
      database,
      `partnerships/${partnershipId}/superlikes/${targetUserId}`,
    );
    let isActive = true;

    const handleChildAdded = (snapshot: DataSnapshot) => {
      if (!isActive) {
        return;
      }
      try {
        if (!snapshot.exists()) {
          return;
        }

        const cardIdentifier = snapshot.key;
        const value = snapshot.val();

        if (!cardIdentifier || !value || typeof value !== "object") {
          return;
        }

        const status = (value as { status?: unknown }).status;
        const createdAt = (value as { createdAt?: unknown }).createdAt;
        if (
          status !== "pending" ||
          typeof createdAt !== "number" ||
          !Number.isFinite(createdAt)
        ) {
          return;
        }

        onPendingSuperlikeAdded({ cardIdentifier, createdAt });
      } catch (error) {
        if (isActive) {
          onError(PartnershipErrorConverter.domainModel(error));
        }
      }
    };

    const onErrorCallback = (error: Error) => {
      if (!isActive) {
        return;
      }
      if (
        SessionTeardownService.isTeardownInProgress() ||
        !AuthenticationService.getInstance().isUserLoggedIn
      ) {
        return;
      }
      onError(PartnershipErrorConverter.domainModel(error));
    };

    const unsubscribe = onChildAdded(
      superlikesRef,
      handleChildAdded,
      onErrorCallback,
    );

    return () => {
      isActive = false;
      unsubscribe();
    };
  }
}
